import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export const GAME_LABELS = {
  lightspeed: 'LightSpeed ⚡',
  cirquiz:    'CirQuiz 🏎️',
  podiumquiz: 'PodiumQuiz 🏆',
};

export const GAME_XP = {
  lightspeed: 1000,
  cirquiz:    500,
  podiumquiz: 500,
};

export default function useGameChallenge(game) {
  const { user } = useAuth();
  const [activeChallenge, setActiveChallenge] = useState(null);
  const [submitting, setSubmitting]           = useState(false);
  const pollRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const res = await api.get(`/challenges/active/${game}`);
      setActiveChallenge(prev => {
        if (res.data === null && prev?.status === 'completed') return prev;
        return res.data;
      });
    } catch {}
  }, [user, game]);

  useEffect(() => { refresh(); }, [refresh]);

  // Auto-poll every 8s once we submitted, to detect when opponent finishes
  useEffect(() => {
    const amChallenger = activeChallenge && user
      ? String(activeChallenge.challenger?._id) === String(user.id) : false;
    const myStored = activeChallenge
      ? (amChallenger ? activeChallenge.challengerScore : activeChallenge.challengedScore)
      : null;
    const isWaiting = activeChallenge && myStored !== null && activeChallenge.status !== 'completed';

    clearInterval(pollRef.current);
    if (isWaiting) {
      pollRef.current = setInterval(refresh, 8000);
    }
    return () => clearInterval(pollRef.current);
  }, [activeChallenge, user, refresh]);

  const submitScore = useCallback(async (score) => {
    if (!activeChallenge) return null;
    setSubmitting(true);
    try {
      const res = await api.post(`/challenges/${activeChallenge._id}/score`, { score });
      setActiveChallenge(res.data);
      return res.data;
    } catch (err) {
      return { error: err?.response?.data?.message || 'Erreur' };
    } finally { setSubmitting(false); }
  }, [activeChallenge]);

  const amChallenger = activeChallenge && user
    ? String(activeChallenge.challenger?._id) === String(user.id) : false;

  const opponent = activeChallenge && user
    ? (amChallenger ? activeChallenge.challenged : activeChallenge.challenger)
    : null;

  const myScore = activeChallenge
    ? (amChallenger ? activeChallenge.challengerScore : activeChallenge.challengedScore)
    : null;

  const opponentScore = activeChallenge
    ? (amChallenger ? activeChallenge.challengedScore : activeChallenge.challengerScore)
    : null;

  const hasSubmitted = myScore !== null;
  const isCompleted  = activeChallenge?.status === 'completed';
  const iWon = isCompleted && activeChallenge?.winner
    ? String(activeChallenge.winner?._id ?? activeChallenge.winner) === String(user?.id)
    : false;

  return {
    activeChallenge, opponent, myScore, opponentScore,
    hasSubmitted, isCompleted, iWon,
    submitScore, refresh, submitting,
  };
}
